import { BusinessObjectBase, BusinessObjectPartContext, FeatureFlagsRulesServiceBase, ObservableCollection, PropertyChangedJoinPoint, QueryBus, RuleContext, Weaver } from '@skylab-shared/core';
import { CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, ProductModel, SaleAdvanceDocumentItemModel, SaleDocumentItemModel, SaleDocumentKind, SalePatternModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { ContextKeys } from '../../common/context-keys';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery, SaleCommunityTransactionItem } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CurrencyCollectionQuery } from '../../currency/queries/currency.query-base';
import { DocumentItemsValidationsBoPart, DocumentItemsValidationsBoPartModelPart } from '../../documents/bo-parts/document-items-validations.bo-part';
import { QuantityLessThanZeroError } from '../../documents/violations/quantity-less-than-zero-error';
import { ProductRepositoryContract } from '../../shared-business-temporary-contracts';
import { SplitPaymentFlag } from '../sale-feature-flags-rules.service-base';
import { isAdvanceModel, isSalePatternModel } from '../utils/sale-utils';
import { CostGreaterThanPriceError } from '../violations/cost-greater-than-price-error';
import { ProductOnDocumentWithReverseChargeCommunityTransactionWarning } from '../violations/product-on-document-with-reverse-charge-community-transaction-warning';
import { ProductSubjectedToReverseChargeOnDocumentWarning } from '../violations/product-subjected-to-reverse-charge-on-document-warning';
import { SaleDocumentItemPriceShouldNotBeLessThanZero } from '../violations/sale-document-item-price-should-not-be-less-than-zero-warning';

interface SaleDocumentModelPart extends DocumentItemsValidationsBoPartModelPart {
    communityTransactionId: number;
    items: ObservableCollection<SaleDocumentItemModel>;
    exchangeRate: ExchangeRateModel;
    currencyId: number;
    kind: SaleDocumentKind;
    taxLiabilityDate: Date;
    issueDate: Date;
    payments: ObservableCollection<DocumentPaymentListItemModel>;
}

export class SaleDocumentItemsValidationsBoPart extends DocumentItemsValidationsBoPart<SaleDocumentModelPart> {
    public constructor(
        weaver: Weaver,
        protected productRepository: ProductRepositoryContract,
        protected featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected queryBus: QueryBus
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<SaleDocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);

        this.registerReadonlyRules();
        this.registerInventoryReadonlyRules();
        this.registerPriceValidationRules();
        this.registerCostValidationRules();
        this.registerCheckItemOnReverseChargeDocumentValidationRules();
        this.registerQuantityValidationRules();
    }

    protected registerReadonlyRules(): void {
        const itemModel = isAdvanceModel(this.bo.model) ? SaleAdvanceDocumentItemModel : SaleDocumentItemModel;
        this.engine.readonly.setAsReadonly([itemModel, 'value_vatAfterDiscount']);
    }

    private registerInventoryReadonlyRules(): void {
        if (isSalePatternModel(this.bo.model)) return;

        const itemModel = isAdvanceModel(this.bo.model) ? SaleAdvanceDocumentItemModel : SaleDocumentItemModel;

        this.engine.readonly.addRule(
            [[itemModel, 'productId'], [itemModel, 'productName'], [itemModel, 'unitId']],
            [[itemModel, 'productName'], [itemModel, 'unitId'],
        ], ctx => ctx.source.includesInventoryControl);
    }

    private registerPriceValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod'])(
            () => {
                this.engine.validation.queueValidation([SaleDocumentItemModel, 'price_grossBeforeDiscount']);
                this.engine.validation.queueValidation([SaleDocumentItemModel, 'price_netBeforeDiscount']);
            }
        )));

        this.engine.validation.addRule(
            [[SaleDocumentItemModel, 'price_grossBeforeDiscount']],
            [[SaleDocumentItemModel, 'price_grossBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Net) return true;
                return ctx.source.price_grossBeforeDiscount >= 0;
            },
            SaleDocumentItemPriceShouldNotBeLessThanZero
        );

        this.engine.validation.addRule(
            [[SaleDocumentItemModel, 'price_netBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Gross) return true;
                return ctx.source.price_netBeforeDiscount >= 0;
            },
            SaleDocumentItemPriceShouldNotBeLessThanZero
        );
    }

    private registerQuantityValidationRules(): void {
        this.engine.validation.addRule(
            [[SaleDocumentItemModel, 'quantity']],
            ctx => ctx.source.quantity >= 0,
            QuantityLessThanZeroError
        );
    }

    private registerCostValidationRules(): void {
        this.engine.validation.addRule(
            [[SaleDocumentItemModel, 'unitCost'], [SaleDocumentItemModel, 'price_grossAfterDiscount']],
            [[SaleDocumentItemModel, 'price_grossBeforeDiscount']],
            ctx => {
                if (this.bo.model.kind !== SaleDocumentKind.VatMarginInvoice && this.bo.model.kind !== SaleDocumentKind.VatMarginInvoiceAdjustment) {
                    return true;
                }
                const rate = this.bo.model.exchangeRate
                    ? this.bo.model.exchangeRate.rate / this.bo.model.exchangeRate.units
                    : 1;
                const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
                const unitCost = Calc.round(ctx.source.unitCost / rate, precision, MidpointRounding.AwayFromZero);
                return !(unitCost > ctx.source.price_grossAfterDiscount);
            },
            CostGreaterThanPriceError
        );
    }

    private registerCheckItemOnReverseChargeDocumentValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(SaleDocumentItemModel, ['productId'])(
            () => this.engine.validation.queueValidation([SaleDocumentItemModel, 'productName'])
        )));

        const validate = (ctx: RuleContext<SaleDocumentModelPart, SaleDocumentItemModel, SaleDocumentModelPart, BusinessObjectBase<SaleDocumentModelPart>>) => async (condition: (ct: SaleCommunityTransactionItem, p: ProductModel) => boolean) => {
            if (!this.splitPaymentFeatureFlagDate || this.isSplitPaymentEnabled(this.splitPaymentFeatureFlagDate))
                return true;
            const item = <SaleDocumentItemModel>ctx.target;
            if (!item.productId)
                return true;

            const communityTransaction = this.bo.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { id: ctx.source.communityTransactionId });
            if (!communityTransaction) return true;

            const product = await this.bo.localContext.getAsync(ContextKeys.product(item.productId), () => this.productRepository.findAsync(item.productId!));
            return condition(communityTransaction, product);
        };

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'taxLiabilityDate'], [this.modelType, 'issueDate']],
            [[SaleDocumentItemModel, 'productName']],
            ctx => validate(ctx)((ct, p) => !ct.reverseCharge || p.subjectToReverseCharge),
            ProductOnDocumentWithReverseChargeCommunityTransactionWarning
        );

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'taxLiabilityDate'], [this.modelType, 'issueDate']],
            [[SaleDocumentItemModel, 'productName']],
            ctx => validate(ctx)((ct, p) => ct.reverseCharge || !p.subjectToReverseCharge),
            ProductSubjectedToReverseChargeOnDocumentWarning
        );
    }

    private isSplitPaymentEnabled(date: Date) {
        return this.featureFlagsRulesService.getFlagStatus(SplitPaymentFlag, date);
    }

    private get splitPaymentFeatureFlagDate(): Date {
        return this.bo.model instanceof SalePatternModel
            ? new Date()
            : this.bo.model.kind === SaleDocumentKind.ProForma
                ? this.bo.model.issueDate
                : this.bo.model.taxLiabilityDate;
    }
}
